import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  InjectionToken,
  Input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService, IEmailVerificationChallenge, ISecondFactorChallenge } from '@services/auth.service';
import { Observable, Subscription, timer } from 'rxjs';
import { goneReason, normaliseCode, parseErrorBody, retryTime } from '../login-responses';

/**
 * Why the step handed back to the password form. `completed` means the challenge was finished
 * elsewhere (the email link, another tab); the sign-in page decides what this browser does about it.
 */
export type VerificationEnd =
  | 'expired'
  | 'completed'
  | 'rejected'
  | 'reset-required'
  | 'back'
  | 'too-many-codes';

/**
 * Milliseconds between sends, so a double press or an impatient user does not spend the challenge's
 * three sends. A token so that stories and tests need not wait it out.
 */
export const RESEND_PAUSE_MS = new InjectionToken<number>('RESEND_PAUSE_MS', {
  providedIn: 'root',
  factory: () => 30_000,
});

interface Notice {
  key: string;
  params?: Record<string, unknown>;
  tone: 'error' | 'info';
}

/**
 * The emailed-code step a correct password leads to for an unverified address (openspec
 * require-verified-email-at-signin, design section 4), or for a verified user on a browser that is not
 * trusted (section 10, kind `new-device`): the same step, worded for each. It asks for an address first
 * when none is on file. Opened from the email's link, the code is filled in and never submitted on load
 * (section 4.1).
 */
@Component({
  selector: 'app-email-verification-step',
  templateUrl: './email-verification-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col gap-4' },
})
export class EmailVerificationStepComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() challenge!: IEmailVerificationChallenge;
  /** The code from the email link, if the page was opened from it. */
  @Input() linkCode: string | null = null;
  @Output() signedIn = new EventEmitter<void>();
  /** The address is proven, and a second factor comes next, with no session yet. */
  @Output() secondFactor = new EventEmitter<ISecondFactorChallenge>();
  @Output() ended = new EventEmitter<VerificationEnd>();

  @ViewChild('firstField') private firstField?: ElementRef<HTMLInputElement>;

  readonly codeForm = new FormGroup({
    code: new FormControl('', { nonNullable: true }),
  });
  readonly addressForm = new FormGroup({
    emailAddress: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
  });

  step: 'address' | 'code' = 'code';
  /** Where the code went: masked from the server, or as the user typed it. */
  sentTo: string | null = null;
  /** The user typed `sentTo` themselves, so they may change it; an address on file only an administrator can. */
  enteredAddress = false;
  notice: Notice | null = null;
  busy = false;
  resendPaused = false;
  sendLimitReached = false;

  private resendPause?: Subscription;

  constructor(
    private _as: AuthService,
    private _cdr: ChangeDetectorRef,
    @Inject(RESEND_PAUSE_MS) private _resendPauseMs: number,
    @Inject(LOCALE_ID) private _locale: string
  ) {}

  get isNewDevice(): boolean {
    return this.challenge.kind === 'new-device';
  }

  get codeIsComplete(): boolean {
    return /^\d{6}$/.test(normaliseCode(this.codeForm.controls.code.value));
  }

  ngOnInit() {
    this.step = this.challenge.addressRequired ? 'address' : 'code';
    this.sentTo = this.challenge.addressHint;
    if (this.linkCode) {
      this.codeForm.controls.code.setValue(this.linkCode);
    } else if (this.step === 'code') {
      // The password step has just sent the first code.
      this.pauseResend();
    }
  }

  ngAfterViewInit() {
    // After this check, not during it: focus floats the field's label, which would change a binding
    // Angular has just checked.
    setTimeout(() => this.firstField?.nativeElement.focus());
  }

  ngOnDestroy() {
    this.resendPause?.unsubscribe();
  }

  verify() {
    if (this.busy || !this.codeIsComplete) {
      return;
    }
    const code = normaliseCode(this.codeForm.controls.code.value);
    this.start();
    this._as.verifyEmailVerificationCode(this.challenge.challenge, code).subscribe({
      next: answer => {
        if (answer === true) {
          this.signedIn.emit();
        } else if (answer) {
          this.secondFactor.emit(answer);
        } else {
          this.ended.emit('rejected');
        }
      },
      error: (error: HttpErrorResponse) => this.onVerifyError(error, code),
    });
  }

  resend() {
    this.send(this._as.sendEmailVerificationCode(this.challenge.challenge), () => {
      this.notice = { key: 'Login.CodeResent', tone: 'info' };
    });
  }

  submitAddress() {
    if (this.busy || this.addressForm.invalid) {
      return;
    }
    const address = this.addressForm.controls.emailAddress.value.trim();
    this.send(this._as.setEmailVerificationAddress(this.challenge.challenge, address), () => {
      this.step = 'code';
      this.sentTo = address;
      this.enteredAddress = true;
      this.codeForm.reset();
    });
  }

  useDifferentAddress() {
    this.step = 'address';
    this.notice = null;
  }

  back() {
    this.ended.emit('back');
  }

  private start() {
    this.busy = true;
    this.notice = null;
    this._cdr.markForCheck();
  }

  private send(request: Observable<unknown>, onSent: () => void) {
    this.start();
    request.subscribe({
      next: () => {
        this.busy = false;
        onSent();
        this.pauseResend();
        this._cdr.markForCheck();
      },
      error: (error: HttpErrorResponse) => {
        this.busy = false;
        this._cdr.markForCheck();
        if (error.status === 410) {
          this.ended.emit(goneReason(error));
          return;
        }
        if (error.status === 429) {
          // Either way the code already sent still works, so the step stays open.
          const body = parseErrorBody(error.error);
          this.sendLimitReached = true;
          this.notice =
            body?.limit === 'emails'
              ? { key: 'Login.CodeLimitResend', tone: 'error', params: { time: retryTime(body.retryAfter, this._locale) } }
              : { key: 'Login.CodeLimitChallenge', tone: 'error' };
          return;
        }
        // The address endpoint's one 400 is an address it cannot send to.
        const invalidAddress = error.status === 400 && this.step === 'address';
        this.notice = {
          key: invalidAddress ? 'Login.AddressInvalid' : 'Login.Failed',
          tone: 'error',
        };
      },
    });
  }

  private onVerifyError(error: HttpErrorResponse, code: string) {
    this.busy = false;
    this._cdr.markForCheck();
    if (error.status === 410) {
      this.ended.emit(goneReason(error));
      return;
    }

    const body = parseErrorBody(error.error);
    if (error.status === 400 && typeof body?.attemptsLeft === 'number') {
      // The fifth wrong code ends the challenge, and reads the same as one that ran out.
      if (body.attemptsLeft === 0) {
        this.ended.emit('expired');
        return;
      }
      // A link from before a resend carries a replaced code. It counts as a wrong code all the same.
      this.notice =
        code === this.linkCode
          ? { key: 'Login.CodeFromOlderEmail', tone: 'error' }
          : {
              key: body.attemptsLeft === 1 ? 'Login.CodeWrongLast' : 'Login.CodeWrong',
              params: { attempts: body.attemptsLeft },
              tone: 'error',
            };
      return;
    }

    // The code was right, and the account was checked again: the answers a password gets.
    if (body?.passwordResetRequired) {
      this.ended.emit('reset-required');
    } else if (body?.success === false) {
      this.ended.emit('rejected');
    } else {
      this.notice = { key: 'Login.Failed', tone: 'error' };
    }
  }

  private pauseResend() {
    this.resendPause?.unsubscribe();
    this.resendPaused = true;
    this.resendPause = timer(this._resendPauseMs).subscribe(() => {
      this.resendPaused = false;
      this._cdr.markForCheck();
    });
  }
}
